/**
 * Human-support bridge client. The AI chat escalates to a human when the
 * model emits the [[HUMAN_HELP]] marker; from then on the conversation id
 * is held in localStorage and user messages are mirrored to the support
 * worker (→ Telegram), whose replies are polled back into the chat.
 */

import { clientConfig } from "@quickpoint.me/shared";
import { getAuth } from "firebase/auth";
import type { ChatMessage } from "../state/aiChatAtoms";

export const HUMAN_HELP_MARKER = "[[HUMAN_HELP]]";
const STORAGE_KEY = "qp_support_conversation";

export interface SupportMessage {
	id: number;
	sender: "user" | "human";
	content: string;
	created_at: number;
}

export function getActiveConversationId(): string | null {
	try {
		return localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

function setActiveConversationId(id: string | null): void {
	try {
		id ? localStorage.setItem(STORAGE_KEY, id) : localStorage.removeItem(STORAGE_KEY);
	} catch {
		/* private mode */
	}
}

/** Open a conversation with the transcript tail; remembers its id locally. */
export async function escalateToHuman(
	stackCfKey: string,
	transcript: ChatMessage[],
): Promise<string | null> {
	const auth = getAuth();
	const jwt = await auth.currentUser?.getIdToken();
	if (!jwt) return null;

	try {
		const res = await fetch(`${clientConfig.support()}/escalate`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				jwt,
				email: auth.currentUser?.email ?? undefined,
				stack: stackCfKey,
				transcript: transcript.map((m) => ({
					role: m.role,
					content: m.content,
				})),
			}),
		});
		if (!res.ok) return null;
		const body = (await res.json()) as { conversationId?: string };
		if (body.conversationId) {
			setActiveConversationId(body.conversationId);
			return body.conversationId;
		}
		return null;
	} catch {
		return null;
	}
}

/** Mirror a user chat message into the open conversation (fire-and-forget). */
export function forwardToHuman(content: string): void {
	const id = getActiveConversationId();
	if (!id) return;
	void fetch(`${clientConfig.support()}/message`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ conversationId: id, content }),
	}).then((res) => {
		// conversation was closed from the Telegram side — stop mirroring
		if (res.status === 409 || res.status === 404) setActiveConversationId(null);
	}).catch(() => {});
}

/** Poll for new messages after the given row id. Clears local state on close. */
export async function fetchSupportMessages(
	afterId: number,
): Promise<{ messages: SupportMessage[]; closed: boolean } | null> {
	const id = getActiveConversationId();
	if (!id) return null;
	try {
		const res = await fetch(
			`${clientConfig.support()}/messages/${id}?after=${afterId}`,
		);
		if (res.status === 404) {
			setActiveConversationId(null);
			return null;
		}
		if (!res.ok) return null;
		const body = (await res.json()) as {
			status: string;
			messages: SupportMessage[];
		};
		const closed = body.status !== "open";
		if (closed) setActiveConversationId(null);
		return { messages: body.messages ?? [], closed };
	} catch {
		return null;
	}
}
